import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { apiClient } from "../lib/api";
import { Ghost } from "lucide-react";
import StudyMateLogo from "../components/StudyMateLogo";
import { validateUsername } from "../lib/username";

// Google icon SVG component
function GoogleIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

export function LoginPage() {
  const { login, playAsGuest } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = loc?.state?.from || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(email, password); nav(dest); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  
  const guest = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post("/auth/guest");
      localStorage.setItem("ba_token", r.data.token);
      await playAsGuest(r.data);
      nav(dest);
    } catch (err) { toast.error("Guest sign-in failed"); }
    finally { setBusy(false); }
  };

  const googleSignIn = () => {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backendUrl}/api/auth/google/login?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };
  
  return <AuthShell title="SIGN IN" subtitle="Resume your climb">
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <input className="ba-input" placeholder="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="login-email" />
      <input className="ba-input" placeholder="password" type="password" value={password} onChange={e=>setPassword(e.target.value)} required data-testid="login-password" />
      <button type="submit" disabled={busy} className="ba-btn-primary w-full py-3" data-testid="login-submit">{busy?"...":"ENGAGE"}</button>
      
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-[#0B0C10] px-2 text-zinc-500 font-mono tracking-wider">or</span>
        </div>
      </div>

      {/* Google Sign In Button */}
      <button 
        type="button" 
        onClick={googleSignIn} 
        disabled={busy}
        className="w-full px-5 py-3 rounded-xl border border-cyan/20 bg-gradient-to-r from-cyan/5 to-blue-500/5 hover:from-cyan/10 hover:to-blue-500/10 transition-all font-mono uppercase tracking-[0.2em] text-xs text-white/90 flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg shadow-cyan/5 hover:shadow-cyan/10"
        data-testid="login-google"
      >
        <GoogleIcon size={16} />
        <span>Continue with Google</span>
      </button>

      <button type="button" onClick={guest} disabled={busy} className="w-full px-5 py-3 rounded-xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] transition-colors font-mono uppercase tracking-[0.25em] text-xs text-white/85 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-guest">
        <Ghost size={14} className="text-purple"/> PLAY AS GUEST
      </button>
      <div className="text-center text-sm text-zinc-400">No account? <Link to="/signup" state={{ from: dest }} className="text-cyan hover:text-glow-cyan" data-testid="goto-signup">Create one</Link></div>
    </form>
  </AuthShell>;
}

export function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const dest = loc?.state?.from || "/";
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  
  const submit = async (e) => {
    e.preventDefault();
    const check = validateUsername(username);
    if (!check.ok) { toast.error(check.error); return; }
    setBusy(true);
    try { await register(email, check.value, password); nav(dest); }
    catch (err) { toast.error(err?.response?.data?.detail || "Signup failed"); }
    finally { setBusy(false); }
  };

  const googleSignUp = () => {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${backendUrl}/api/auth/google/login?redirect_uri=${encodeURIComponent(redirectUri)}`;
  };
  
  return <AuthShell title="CREATE ACCOUNT" subtitle="Forge your identity">
    <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
      <input className="ba-input" placeholder="username" value={username} onChange={e=>setUsername(e.target.value)} required minLength={3} data-testid="signup-username" />
      <input className="ba-input" placeholder="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required data-testid="signup-email" />
      <input className="ba-input" placeholder="password (min 6)" type="password" value={password} onChange={e=>setPassword(e.target.value)} required minLength={6} data-testid="signup-password" />
      <button type="submit" disabled={busy} className="ba-btn-primary w-full py-3" data-testid="signup-submit">{busy?"...":"INITIATE"}</button>
      
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10"></div>
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-[#0B0C10] px-2 text-zinc-500 font-mono tracking-wider">or</span>
        </div>
      </div>

      {/* Google Sign Up Button */}
      <button 
        type="button" 
        onClick={googleSignUp} 
        disabled={busy}
        className="w-full px-5 py-3 rounded-xl border border-cyan/20 bg-gradient-to-r from-cyan/5 to-blue-500/5 hover:from-cyan/10 hover:to-blue-500/10 transition-all font-mono uppercase tracking-[0.2em] text-xs text-white/90 flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg shadow-cyan/5 hover:shadow-cyan/10"
        data-testid="signup-google"
      >
        <GoogleIcon size={16} />
        <span>Sign up with Google</span>
      </button>

      <div className="text-center text-sm text-zinc-400">Have one? <Link to="/login" state={{ from: dest }} className="text-cyan" data-testid="goto-login">Sign in</Link></div>
    </form>
  </AuthShell>;
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid place-items-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-30"/>
      <motion.div initial={{opacity:0, y:20}} animate={{opacity:1,y:0}} className="ba-card w-full max-w-md p-8 relative">
        <div className="flex justify-center mb-5"><StudyMateLogo compact /></div>
        <div className="font-mono text-[10px] tracking-[0.4em] text-cyan uppercase">{subtitle}</div>
        <h1 className="font-heading font-black text-3xl mt-2 mb-6">{title}</h1>
        {children}
      </motion.div>
    </div>
  );
}
